import {
  CircularProgress,
  Table as MUITable,
  TableBody as MUITableBody,
  TableCell as MUITableCell,
  TableContainer as MUITableContainer,
  TableFooter as MUITableFooter,
  TableRow as MUITableRow,
  styled,
} from "@material-ui/core";
import React, {
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import TableBody from "./TableBody";
import TableHeader from "./TableHeader";
import TablePagination from "./TablePagination";
import type {
  EditingTableRow,
  TableActions,
  TableProps,
  TableRowDefinition,
} from "./types";
import { computeColumnTotals } from "./util";

function Loading({ columns }: { columns: number }) {
  return (
    <MUITableBody>
      <MUITableRow>
        <MUITableCell
          align="center"
          colSpan={columns}
          data-testid="table-loading"
        >
          <CircularProgress />
        </MUITableCell>
      </MUITableRow>
    </MUITableBody>
  );
}

const EmptyText = styled("strong")({
  maxWidth: 700,
  textAlign: "center",
  marginInline: "auto",
});

function Empty({
  columns,
  message = "No data.",
}: {
  columns: number;
  message: Pick<TableProps, "noDataMessage">["noDataMessage"];
}) {
  return (
    <MUITableBody>
      <MUITableRow key="empty-row">
        <MUITableCell key="empty-cell" align="center" colSpan={columns}>
          {typeof message === "function" ? (
            message
          ) : (
            <EmptyText>{message}</EmptyText>
          )}
        </MUITableCell>
      </MUITableRow>
    </MUITableBody>
  );
}

const FooterDivider = styled(MUITableRow)({
  height: "8px",
  borderBottom: "1px solid rgba(224, 224, 224, 1)",
});

export const Table = React.forwardRef(
  (tableProps: TableProps, ref: React.Ref<TableActions>) => {
    const {
      rows,
      columns,
      loading,
      pagination,
      editing,
      stickyHeader,
      classes,
      expandingGroups,
      noDataMessage,
      dense = false,
      tableElementProps,
      hideHeader = false,
      dataTestId,
      minTableWidth,
    } = tableProps;

    const containerRef = useRef<HTMLDivElement>(null);
    const [isOverflowing, setIsOverflowing] = useState(false);

    // useLayoutEffect so the overflow check + shadow class land before paint —
    // otherwise the shadow can flash on/off on the first frame.
    useLayoutEffect(() => {
      const el = containerRef.current;
      if (!el) return undefined;
      const update = () => setIsOverflowing(el.scrollWidth > el.clientWidth);
      update();
      if (typeof ResizeObserver === "undefined") return undefined;
      const observer = new ResizeObserver(update);
      observer.observe(el);
      const inner = el.querySelector("table");
      if (inner) observer.observe(inner);
      return () => observer.disconnect();
    }, []);

    const effectiveColumns = useMemo(() => {
      if (!expandingGroups) return columns;
      const alreadyPresent = columns.some((col) => col.field === "__expander");
      if (alreadyPresent) return columns;
      return [
        {
          field: "__expander",
          width: 35,
          padding: "0 8px",
          alignContents: "center" as const,
        },
        ...columns,
      ];
    }, [columns, expandingGroups]);

    const columnTotals = useMemo(
      () => computeColumnTotals(effectiveColumns),
      [effectiveColumns],
    );

    const numRows = pagination?.totalRows || rows.length;

    const [editedRow, setEditedRow] = useState(
      (rows[0] || {}) as EditingTableRow,
    );
    const internalRef = ref;

    useImperativeHandle(internalRef, () => ({
      startEditRow: (index: number) => {
        setEditedRow({ ...(rows[index] as TableRowDefinition), index });
      },
      cancelEditRow: (index: number) => {
        setEditedRow({ ...(rows[index] as TableRowDefinition), index: null });
      },
      saveEditRow: async () => {
        try {
          await editing.onRowSave(
            editedRow.id,
            editedRow as TableRowDefinition,
          );

          setEditedRow({ ...editedRow, index: null });
          return true;
        } catch (err) {
          // We need developers to be able to see errors to debug

          console.error(err);
          return false;
        }
      },
    }));

    const hidePagination =
      loading ||
      (rows.length === numRows && numRows === pagination?.pageSize) ||
      !pagination;

    const renderTableContents = () => {
      if (loading) return <Loading columns={effectiveColumns.length} />;
      if (!rows.length && !loading)
        return (
          <Empty columns={effectiveColumns.length} message={noDataMessage} />
        );
      return (
        <TableBody
          {...tableProps}
          columns={effectiveColumns}
          columnTotals={columnTotals}
          editedRow={editedRow}
          setEditedRow={setEditedRow}
        />
      );
    };

    return (
      <MUITableContainer
        ref={containerRef}
        style={{
          overflowX: "auto",
          width: "100%",
          ["--sticky-shadow" as string]: isOverflowing
            ? "4px 0 6px -2px rgba(0, 0, 0, 0.15)"
            : "none",
        }}
        data-testid={dataTestId}
      >
        <MUITable
          stickyHeader={stickyHeader}
          size={dense ? "small" : "medium"}
          style={
            (minTableWidth ?? 0) > 0
              ? {
                  minWidth: minTableWidth,
                  overflowX: "scroll",
                  tableLayout: "fixed",
                }
              : { minWidth: "100%", overflowX: "scroll" }
          }
          {...tableElementProps}
        >
          {!hideHeader && (
            <TableHeader
              columns={effectiveColumns}
              classes={classes}
              minTableWidth={minTableWidth}
              columnTotals={columnTotals}
            />
          )}
          {renderTableContents()}
          {!hidePagination && (
            <MUITableFooter>
              <FooterDivider />
              <TablePagination
                numRows={numRows}
                {...tableProps}
                columns={effectiveColumns}
                editedRow={editedRow}
                setEditedRow={setEditedRow}
              />
            </MUITableFooter>
          )}
        </MUITable>
      </MUITableContainer>
    );
  },
);
